import * as React from "react";
import { graphql } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Media } from "../components/Media";
import Seo from "../components/Seo";
import AussieOwned from "../components/seals/AustralianOwned";
import Vegan from "../components/seals/VeganFriendly";
import GlutenFree from "../components/seals/GlutenFree";
import OneADay from "../components/seals/OneADay";
import StickyWrapper from "../components/StickyWrapper";
import AstaxanthinStats from "../components/youBeauty/AstaxanthinStats";
import Ingredients from "../components/youBeauty/Ingredients";
import CustomerQuote from "../components/CustomerQuote";
import ogImage from "../images/youBeauty/you-beauty-happy-woman.jpg";
import "../styles/youBeauty.css";

const YouBeauty = ({ data }) => {
  const product = getImage(data.product);
  const products = getImage(data.products);
  const beach = getImage(data.beach);
  const woman = getImage(data.woman);
  const powder = getImage(data.powder);
  const astareal = getImage(data.astareal);
  const pushups = getImage(data.pushups);
  const glasses = getImage(data.glasses);
  const hair = getImage(data.hair);
  const nails = getImage(data.nails);
  const capsule = getImage(data.capsule);

  return (
    <>
      <Seo
        title="You Beauty™: Healthy Skin, Hair And Nails"
        description="The plant powered capsule that provides antioxidants and minerals to enhance skin regeneration, increase hair growth and nail thickness. Made in Australia."
        image={`https://www.ithrivenutrition.com.au${ogImage}`}
        alt="You Beauty bottle in front of an attractive woman with curly hair."
        handle="you-beauty"
      />
      <div className="bg-gray-darker">
        <div className="max-w-screen-lg m-auto pt-2 md:pt-6 lg:pt-10">
          <StickyWrapper
            title="You Beauty"
            subtitle="Premium Anti-Aging Complex For Healthy Skin, Hair And Nails"
            shopLink="you-beauty"
          />
          <div className="flex flex-wrap-reverse md:flex-nowrap">
            <div className="text-white mt-0 md:mt-10 w-full px-6 md:p-0 md:w-5/12">
              <p>
                <strong>You Beauty™</strong> takes the best of herbal medicine
                and modern science to:
              </p>
              <ul className="list-inside mt-2">
                <li className="list-circle white">
                  Support collagen formation
                </li>
                <li className="list-circle white">
                  Enchance skin regeneration
                </li>
                <li className="list-circle white">
                  Increase hair growth &amp; strength
                </li>
                <li className="list-circle white">Increase nail thickness</li>
                <li className="list-circle white">
                  Reduce free radicals formed in the body
                </li>
              </ul>

              <div className="flex justify-center my-10">
                <AussieOwned fill="#E5154A" />
                <Vegan fill="#E5154A" />
                <GlutenFree fill="#E5154A" />
                <OneADay fill="#E5154A" />
              </div>
            </div>
            <div className="w-full text-right -mb-8 md:mb-0 md:w-7/12 relative">
              <Media lessThan="md">
                <GatsbyImage
                  image={product}
                  alt="You Beauty bottle"
                  className="bottle-hero"
                />
              </Media>
              <Media greaterThanOrEqual="md">
                <GatsbyImage
                  image={products}
                  alt="You Beauty bottles"
                  className="bottles-hero"
                  style={{ position: "absolute" }}
                />
              </Media>
            </div>
          </div>
        </div>
      </div>
      <div className="red-gradient"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 relative overflow-hidden">
        <Media lessThan="md" className="md:hidden">
          <div
            className="relative z-10"
            style={{
              background:
                "linear-gradient(180deg, rgba(204,219,224,1) 0%, rgba(204,219,224,1) 65%, rgba(204,219,224,0) 100%)",
            }}
          >
            <h2 className="text-35 text-center text-blue-dark p-8 pb-16 -mb-16">
              More Than Skin Deep.
            </h2>
          </div>
        </Media>
        <div className="h-96 md:h-full" style={{ maxHeight: "550px" }}>
          <BgImage
            image={beach}
            style={{ height: "100%", backgroundPosition: "80% 40%" }}
          />
        </div>
        <div className="p-4 pb-16 md:p-20 lg:py-36 xl:py-44 2xl:py-48">
          <Media greaterThanOrEqual="md">
            <h2 className="text-40 text-center mb-4 text-blue-dark">
              More Than Skin Deep.
            </h2>
          </Media>
          <p className="max-w-prose mx-auto leading-loose">
            You Beauty™ goes beyond basic skin, hair &amp; nails products by
            providing an optimised combination of natural antioxidants and
            minerals to reduce free radical damage to body cells, commonly
            linked to aging and illness.
          </p>
        </div>
        <Media lessThan="xl">
          <svg className="wave" viewBox="0 0 1699.3 67.8">
            <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" />
              <stop offset="100%" />
            </linearGradient>
            <path
              d="M1699.3,39.3v28.6H0V39.3c71,0,142.2-6.6,212.4-19.7S353.8,0,424.8,0S567,6.6,637.2,19.7s141.4,19.7,212.4,19.7
	s142.2-6.6,212.4-19.7S1203.4,0,1274.4,0s142.2,6.6,212.4,19.7S1628.2,39.3,1699.3,39.3z"
              fill="url(#gradient)"
            />
          </svg>
        </Media>
        <Media greaterThanOrEqual="xl">
          <svg className="wave" viewBox="0 0 1000 16.7">
            <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" />
              <stop offset="100%" />
            </linearGradient>
            <path
              d="M1000,9.7v7H0v-7c41.8,0,83.7-1.6,125-4.9S208.2,0,250,0s83.7,1.6,125,4.9s83.2,4.9,125,4.9s83.7-1.6,125-4.9
	S708.2,0,750,0s83.7,1.6,125,4.9S958.2,9.7,1000,9.7z"
              fill="url(#gradient)"
            />
          </svg>
        </Media>
      </div>
      <div className="flex flex-col md:flex-row relative bg-gradient-to-b from-blue-lighter to-blue-darker items-center">
        <div className="w-4 h-4 rounded-full bg-white particle-1 absolute blur top-3/4 left-1/4"></div>
        <div className="w-2 h-2 rounded-full bg-white particle-2 absolute blur top-1/4 left-1/2"></div>
        <div className="w-3 h-3 rounded-full bg-white particle-3 absolute blur bottom-12 right-1/4"></div>
        <div className="w-4 h-4 rounded-full bg-white particle-4 absolute blur top-1/2 left-12"></div>
        <div className="w-2 h-2 rounded-full bg-white particle-5 absolute blur top-12 left-3/4"></div>
        <div className="w-3 h-3 rounded-full bg-white particle-1 absolute blur bottom-20 right-20"></div>
        <div className="w-2 h-2 rounded-full bg-white particle-2 absolute blur top-20 left-20"></div>
        <div className="w-full md:w-1/2 p-12 md:p-20 text-center">
          <h2 className="text-blue-lightest text-35 md:text-40 mb-2">
            Our Secret?
          </h2>
          <h2 className="text-white text-35 md:text-40">Plant Power.</h2>
          <GatsbyImage
            image={astareal}
            alt="AstaReal logo"
            className="w-36 md:w-44 mt-10"
          />
        </div>
        <div className="w-full md:w-1/2 px-6 pb-6 md:p-20">
          <p className="text-white max-w-prose mx-auto leading-loose mb-6">
            It's well known that antioxidants are good for you, but what is
            unknown to many is the most powerful antioxidant of all:{" "}
            <strong>Astaxanthin.</strong>
          </p>
          <p className="text-white max-w-prose mx-auto leading-loose mb-6">
            Astaxanthin is a red marine algae. It has the strongest free radical
            scavenging activity of any antioxidant on earth.
          </p>
          <p className="text-white max-w-prose mx-auto leading-loose">
            <strong>You Beauty™</strong> is formulated with plant-powered
            AstaReal®, the most clinically studied Astaxanthin in the world.
          </p>
        </div>
      </div>
      <div
        className="h-28 md:h-48 -my-1"
        style={{
          backgroundImage: `url(${powder.images.fallback.src})`,
          backgroundRepeat: "repeat-x",
          backgroundSize: "contain",
        }}
      ></div>
      <div className="bg-gray-darker flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 px-10 md:px-20 flex flex-col justify-between">
          <h2 className="text-35 md:text-40 text-white text-center mt-10 md:mt-20">
            Pretty. Strong.
          </h2>
          <GatsbyImage
            image={woman}
            alt="Woman with healthy skin"
            className="max-w-md mx-auto"
          />
        </div>
        <div className="astaStats w-full md:w-1/2 md:p-16 pb-4">
          <p className="text-white mb-4 px-6 pt-3 md:p-0">
            As an antioxidant, astaxanthin's free radical fighting power is:
          </p>
          <AstaxanthinStats name="almond" />
          <AstaxanthinStats name="tea" />
          <AstaxanthinStats name="peach" />
          <AstaxanthinStats name="grape" />
          <AstaxanthinStats name="orange" />
          <p className="text-gray-lighter text-sm mt-4 md:mt-8 px-6 md:p-0">
            *Nishida Y., et al, (2007), Carotenoid Science, 11, 16-20.
          </p>
        </div>
      </div>
      <div className="flex flex-col-reverse md:relative md:flex-none">
        <div className="w-full md:w-1/2 2xl:w-3/4 p-4 md:p-20 md:relative md:z-10 darkGradient">
          <div className="w-full 2xl:w-3/4">
            <h2 className="text-30 md:text-40 text-white text-center mb-4">
              Exercise Elevated.
            </h2>
            <p className="text-white max-w-prose mx-auto leading-loose">
              Astaxanthin supports your muscles to increase exercise performance
              while also aiding post exercise recovery.
            </p>
          </div>
        </div>
        <div className="h-40 w-full md:w-3/4 xl:w-2/3 2xl:w-1/2 md:h-full md:absolute right-0 top-0 bottom-0 z-0">
          <BgImage
            image={pushups}
            style={{ height: "100%", backgroundPosition: "100% 50%" }}
          />
        </div>
      </div>
      <div className="bg-red">
        <div className="flex flex-col-reverse md:flex-row max-w-screen-xl mx-auto">
          <div className="w-full md:w-1/2 flex flex-col justify-end">
            <GatsbyImage
              image={glasses}
              alt="Woman with healthy bright eyes looking up"
              className="max-w-md mx-auto"
            />
          </div>
          <div className="w-full md:w-1/2 p-4 md:p-20">
            <h2 className="text-30 md:text-40 text-white text-center mb-4">
              Beauty is in the Eye of the Beholder.
            </h2>
            <p className="text-white max-w-prose mx-auto leading-loose">
              Astaxanthin also works to maintain eye health and support healthy
              vision.
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap md:flex-nowrap max-w-screen-xl mx-auto">
        <div className="w-full md:w-1/2 md:p-14">
          <h2 className="text-30 md:text-40 text-center p-4 md:p-0 mb-4 text-gray-light">
            Hair Care in Seconds.
          </h2>
          <p className="text-gray-light px-6 md:p-0">
            Just 1 capsule a day can help:
          </p>
          <ul className="list-inside text-gray-light stroke-current px-6 md:p-0">
            <li className="list-circle gray">Improve hair growth</li>
            <li className="list-circle gray">
              Reduce hair loss in healthy individuals
            </li>
            <li className="list-circle gray">
              Increase hair health and strength
            </li>
          </ul>
          <GatsbyImage
            image={hair}
            alt="Healthy brown hair shaped like a heart shimmering in the sun"
            className="max-w-md mx-auto"
          />
        </div>
        <div className="w-full md:w-1/2 md:p-14 relative">
          <h2 className="text-30 md:text-40 text-center mb-4 mt-10 md:mt-0 text-gray-light">
            Nourish Your Nails.
          </h2>
          <div className="flex md:absolute bottom-0">
            <p className="text-gray-light max-w-prose p-4 md:p-0 md:w-4/6">
              You Beauty is formulated to increase nail strength &amp; decrease
              nail chipping
            </p>
            <GatsbyImage
              image={nails}
              alt="beautiful strong nails using astaxanthin"
              className="w-96 md:w-full"
              style={{ maxWidth: "300px" }}
            />
          </div>
        </div>
      </div>
      <div className="bg-red p-6 md:pb-12">
        <h2
          className="text-30 md:text-40 text-center mt-4"
          style={{ color: "#FFA8A8" }}
        >
          Formulated for
          <br />
          <span className="text-50 md:text-60" style={{ color: "#fff" }}>
            You.
          </span>
        </h2>
        <div className="capsule-grid mt-4 md:-mt-12">
          <GatsbyImage
            image={capsule}
            alt="Close up of You Beauty capsule with astaxanthin"
            className="mx-auto capsule translate-x-1 lg:max-w-md"
          />
          <p className="text-white paragraph leading-8 md:max-w-sm md:border md:border-white md:rounded-full md:rounded-br md:border-opacity-50 md:px-6 md:py-4">
            You Beauty combines the power of AstaReal® with a selection of
            vitamins and minerals to bring out your best.
          </p>
          <Ingredients name="copper" />
          <Ingredients name="vitamin-c" />
          <Ingredients name="silicon" />
          <Ingredients name="zinc" />
          <Ingredients name="vitamin-b7" />
        </div>
      </div>
      <div className="bg-gray-dark p-6">
        <h2 className="text-35 md:text-40 text-white text-center">
          Results That Speak For Themselves.
        </h2>
        <div className="flex flex-col md:flex-row md:justify-center items-center">
          <CustomerQuote name="Brony" color="red" isRight={true}>
            <p>
              ABSOLUTELY LOVE the 'You Beauty' supplements!
              <br />
              After taking one a day for the last week &amp; a half, its done an
              amazing job for my eczema on my hands &amp; scalp!!! Less dry,
              itchy, and sore!! An amazing product. 100% recommend!
            </p>
          </CustomerQuote>
          <CustomerQuote
            name="Britani Watene-Zelezniak"
            color="red"
            isRight={false}
          >
            <p>
              I am in love with 'You Beauty' capsules! I have noticed a big
              difference with my skin since starting to take it -- and my hair
              is so thick and full. I have been taking it for quite a few months
              now and wouldn't look back.
            </p>
          </CustomerQuote>
          <CustomerQuote name="Manon Ligot" color="red" isRight={true}>
            <p>
              I’ve been taking these supplements for a couple of weeks now and
              I’ve already seen a change. It is such a relief to get rid of skin
              issues. I deeply recommend You Beauty.
            </p>
          </CustomerQuote>
        </div>
      </div>
    </>
  );
};

export default YouBeauty;

export const query = graphql`
  query {
    product: file(relativePath: { eq: "youBeauty/you-beauty-bottle.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    products: file(relativePath: { eq: "youBeauty/you-beauty-bottles.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    powder: file(relativePath: { eq: "youBeauty/red-powder.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 500)
      }
    }
    woman: file(relativePath: { eq: "youBeauty/woman-dark-bg.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    astareal: file(relativePath: { eq: "youBeauty/astareal-logo.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    beach: file(relativePath: { eq: "youBeauty/woman-at-the-beach.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    pushups: file(relativePath: { eq: "youBeauty/woman-pushups.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    glasses: file(relativePath: { eq: "youBeauty/astaxanthin-eyes.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    hair: file(relativePath: { eq: "youBeauty/hair.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    nails: file(relativePath: { eq: "youBeauty/nails.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    capsule: file(relativePath: { eq: "youBeauty/you-beauty-close-up.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;
